import {
  Directive,
  ElementRef,
  HostListener,
  Optional,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
@Directive({
  selector: '[appPhoneFormat]',
})
export class PhoneFormatDirective {
  constructor(
    private readonly el: ElementRef<HTMLInputElement>,
    @Optional() @Self() private readonly ngControl: NgControl
  ) {}

  @HostListener('input')
  onInput(): void {
    this.formatAndPreserveCursor();
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    event.preventDefault();

    const input = this.el.nativeElement;
    const pastedText = event.clipboardData?.getData('text') ?? '';

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;

    const nextValue =
      input.value.slice(0, start) + pastedText + input.value.slice(end);

    const digitCursorPosition =
      this.countDigits(input.value.slice(0, start)) +
      this.countDigits(pastedText);

    this.applyFormattedValue(nextValue, digitCursorPosition);
  }

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End',
      'Enter',
    ];

    const isShortcut =
      event.ctrlKey ||
      event.metaKey ||
      event.altKey;

    const isNumber = /^[0-9]$/.test(event.key);

    if (!isNumber && !allowedKeys.includes(event.key) && !isShortcut) {
      event.preventDefault();
    }
  }

  private formatAndPreserveCursor(): void {
    const input = this.el.nativeElement;

    const cursor = input.selectionStart ?? input.value.length;
    const digitCursorPosition = this.countDigits(input.value.slice(0, cursor));

    this.applyFormattedValue(input.value, digitCursorPosition);
  }

  private applyFormattedValue(
    rawValue: string,
    digitCursorPosition: number
  ): void {
    const input = this.el.nativeElement;

    const digits = rawValue.replace(/\D/g, '').slice(0, 10);
    const formatted = this.formatPhoneNumber(digits);

    input.value = formatted;

    this.ngControl.control?.setValue(digits, {
      emitEvent: false,
      emitModelToViewChange: false,
      emitViewToModelChange: false,
    });

    const nextCursorPosition = this.getCursorPositionFromDigitIndex(
      formatted,
      digitCursorPosition
    );

    queueMicrotask(() => {
      input.setSelectionRange(nextCursorPosition, nextCursorPosition);
    });
  }

  private formatPhoneNumber(digits: string): string {
    if (!digits) {
      return '';
    }

    if (digits.length <= 3) {
      return `(${digits}`;
    }

    if (digits.length <= 6) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    }

    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  private countDigits(value: string): number {
    return value.replace(/\D/g, '').length;
  }

  private getCursorPositionFromDigitIndex(
    formattedValue: string,
    digitIndex: number
  ): number {
    if (digitIndex <= 0) {
      return formattedValue.length ? 1 : 0;
    }

    let digitsSeen = 0;

    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        digitsSeen++;

        if (digitsSeen >= digitIndex) {
          return i + 1;
        }
      }
    }

    return formattedValue.length;
  }

}
